import { useEffect, useState } from "react";

import { FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

import {
  AdditionalInsuredInput,
  Relationship,
} from "@covertree/new-business-graphql";
import { useFormWithNotification } from "@covertree/hooks";
import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";

import { Modal } from "@covertree/ui";
import FormTextField from "@covertree/ui/forms/FormTextField";
import { Button, FormToggleCard, Form, FormSelectField } from "@covertree/ui";

import { relationshipOptions, additionalInsuredFormSchema } from "./form";

type Props = {
  firstPersonName?: string;
  checked: boolean;
  onToggle: () => void;
  onSubmit: (formData: AdditionalInsuredInput) => void;
  title?: string;
  subheader?: string;
  tooltip?: string;
  actionText?: string;
  disabled?: boolean;
};

export const FormAddSomeoneElseToggle = ({
  firstPersonName,
  checked,
  onToggle,
  onSubmit,
  title = "Add someone else to the policy",
  subheader,
  tooltip = "You can add a spouse or a second owner to the policy",
  actionText = "Free",
  disabled = false,
}: Props) => {
  const form = useFormWithNotification<AdditionalInsuredInput>({
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      relationship: "" as unknown as Relationship,
    },
    delayError: FORM_ERROR_DELAY_IN_MS,
    reValidateMode: "onSubmit",
    // Can't figure out the reason behind this TS error here
    // eslint-disable-next-line
    // @ts-expect-error
    resolver: yupResolver(additionalInsuredFormSchema),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { control, handleSubmit, reset } = form;

  const handleToggle = () => {
    onToggle();
    if (!checked) {
      setIsModalOpen((prev) => !prev);
    }
  };

  const submit = (formData: AdditionalInsuredInput) => {
    onSubmit(formData);
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isModalOpen) {
      reset();
    }
  }, [isModalOpen]);

  return (
    <>
      <FormToggleCard
        title={title}
        checked={checked}
        onToggle={handleToggle}
        tooltip={tooltip}
        disabled={disabled}
        actionText={actionText}
        subheader={subheader}
      />
      <Modal
        title="Secondary person in the policy"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(e) => e.stopPropagation()}
        maxWidth="sm">
        <FormProvider {...form}>
          <Form onSubmit={handleSubmit(submit)}>
            <Grid
              container
              spacing={3}
              component={Box}
              overflow="hidden"
              pb={3}>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  id="secondary-person-first-name"
                  dataTestId="secondary-person-first-name"
                  name="firstName"
                  label="First name"
                  control={control}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  id="secondary-person-last-name"
                  dataTestId="secondary-person-last-name"
                  name="lastName"
                  label="Last name"
                  control={control}
                />
              </Grid>
              <Grid item xs={12}>
                <FormSelectField
                  id="relationship"
                  name="relationship"
                  dataTestId="relationship-dropdown"
                  label={
                    firstPersonName
                      ? `Relationship to ${firstPersonName}`
                      : "Relationship"
                  }
                  options={relationshipOptions}
                  placeholder="Select..."
                  control={control}
                />
              </Grid>
            </Grid>
            <Box mt={5}>
              <Button type="submit" data-testid="add-person-button">
                Add person
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </Modal>
    </>
  );
};
